<template>
  <div class="short-page">
    <div class="short-shell">
      <div ref="feed" class="short-feed" @scroll.passive="handleFeedScroll">
        <section
          v-for="(item, index) in videoItems"
          :key="item.id || index"
          class="short-slide"
        >
          <div class="short-media">
            <video
              :ref="`video-${index}`"
              class="short-video"
              :poster="item.poster"
              :src="item.url"
              :muted="isMuted"
              loop
              playsinline
              webkit-playsinline="true"
              preload="metadata"
              @click="toggleVideo(index)"
            ></video>

            <div class="short-overlay"></div>

            <header class="short-topbar">
              <button class="brand" type="button" @click="$router.replace('/Home')">
                {{ brandName }}
              </button>
              <b>{{ videoTitle }}</b>
              <button class="mute-btn" type="button" @click="toggleMute">
                <svg v-if="isMuted" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9">
                  <path d="M11 5 6.5 9H3v6h3.5L11 19V5Z"></path>
                  <path d="m16 9 5 5M21 9l-5 5"></path>
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9">
                  <path d="M11 5 6.5 9H3v6h3.5L11 19V5Z"></path>
                  <path d="M15.5 8.5a5 5 0 0 1 0 7"></path>
                  <path d="M18 6a8.5 8.5 0 0 1 0 12"></path>
                </svg>
              </button>
            </header>

            <div class="short-bottom">
              <div class="short-copy">
                <div class="short-author">
                  <span>{{ copy.platformRecommend }}</span>
                  <i>{{ copy.bookable }}</i>
                </div>
                <h2>{{ item.title }}</h2>
                <p class="short-meta">
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 2.8a6.5 6.5 0 0 0-6.5 6.5c0 4.78 5.3 10.84 6.1 11.73a.54.54 0 0 0 .8 0c.8-.89 6.1-6.95 6.1-11.73A6.5 6.5 0 0 0 12 2.8Zm0 8.93a2.43 2.43 0 1 1 0-4.86 2.43 2.43 0 0 1 0 4.86Z"></path>
                  </svg>
                  <span>{{ copy.localSwipe }}</span>
                </p>
              </div>

              <div class="short-actions">
                <button class="contact-btn" type="button" @click="serviceVisible = true">
                  {{ copy.contactHer }}
                </button>
              </div>
            </div>

            <button v-if="index < videoItems.length - 1" class="next-tip" type="button" @click="scrollNext(index)">
              ↑
            </button>

            <div v-if="currentIndex === index && !playingMap[index]" class="pause-mask">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 6h3v12H8zM13 6h3v12h-3z"></path>
              </svg>
            </div>
          </div>
        </section>

        <section v-if="!videoItems.length && !loading" class="short-empty">
          <b>{{ copy.emptyTitle }}</b>
          <span>{{ copy.emptyDesc }}</span>
        </section>
      </div>
    </div>

    <Footer :is_show="true" />

    <van-popup v-model="serviceVisible" class="service-popup" round>
      <div class="service-card">
        <button class="service-close" type="button" @click="serviceVisible = false">×</button>
        <div class="service-icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M21.7 3.2 18.5 20c-.2 1.2-.9 1.5-1.9.9l-4.8-3.5-2.3 2.2c-.3.3-.5.5-1 .5l.3-4.9 8.9-8c.4-.3-.1-.5-.6-.2L6.1 13.9 1.4 12.4c-1-.3-1-1 .2-1.5L20 3.8c.9-.3 1.8.2 1.7-.6Z"
            ></path>
          </svg>
        </div>
        <h3>{{ kefuTitle }}</h3>
        <p class="service-sub">{{ copy.serviceDesc }}</p>

        <button
          v-for="agent in serviceAgents"
          :key="agent.name"
          class="service-item"
          type="button"
          @click="openAgent(agent.link)"
        >
          <span class="service-item-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M21.7 3.2 18.5 20c-.2 1.2-.9 1.5-1.9.9l-4.8-3.5-2.3 2.2c-.3.3-.5.5-1 .5l.3-4.9 8.9-8c.4-.3-.1-.5-.6-.2L6.1 13.9 1.4 12.4c-1-.3-1-1 .2-1.5L20 3.8c.9-.3 1.8.2 1.7-.6Z"
              ></path>
            </svg>
          </span>
          <span class="service-item-copy">
            <b>{{ agent.name }}</b>
            <small>{{ copy.serviceOnline }}</small>
          </span>
          <em>{{ kefuActionText }}</em>
          <span class="service-arrow">›</span>
        </button>

        <button class="later-btn" type="button" @click="serviceVisible = false">
          {{ copy.serviceLater }}
        </button>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { getPageCopy } from "@/common/pageLocale";
import { resolveAssetUrl } from "@/common/runtimeConfig";
import { getKefuContact, openKefuContact } from "@/common/kefu";
import Footer from "@/common/Footer.vue";

export default {
  components: {
    Footer,
  },
  data() {
    return {
      loading: false,
      page: 0,
      total: 0,
      videoItems: [],
      currentIndex: 0,
      isMuted: true,
      playingMap: {},
      serviceVisible: false,
    };
  },
  computed: {
    copy() {
      return getPageCopy(this, "video");
    },
    baseInfo() {
      return this.$store.getters.getBaseInfo || {};
    },
    ui() {
      return this.baseInfo.ui || {};
    },
    brandName() {
      return String(this.ui.brand || this.baseInfo.name || "OMN")
        .replace(/MShub/gi, "OMN")
        .trim() || "OMN";
    },
    videoTitle() {
      const raw = this.ui.nav_video || this.copy.videoTitle || "看片";
      return String(raw)
        .replace(/MShub/gi, "OMN")
        .replace(/视频/g, "看片")
        .trim() || "看片";
    },
    kefuContact() {
      return getKefuContact(this.baseInfo);
    },
    kefuTitle() {
      return this.kefuContact.title || this.copy.serviceTitle;
    },
    kefuActionText() {
      return this.kefuContact.action_text || this.copy.serviceAction;
    },
    serviceAgents() {
      const contact = this.kefuContact;
      if (contact.url) {
        return [{ name: contact.name || this.copy.serviceExclusiveName, link: contact.url }];
      }
      return [];
    },
  },
  created() {
    this.fetchVideos();
  },
  mounted() {
    window.addEventListener("resize", this.refreshViewportHeight);
    this.$nextTick(() => {
      this.refreshViewportHeight();
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.refreshViewportHeight);
    this.pauseAll();
  },
  methods: {
    async fetchVideos(reset = true) {
      if (this.loading) {
        return;
      }
      this.loading = true;
      try {
        const nextPage = reset ? 0 : this.page;
        const res = await this.$http({
          method: "get",
          data: { id: 0, page: nextPage },
          url: "video_list",
        });
        const payload = res && res.data ? res.data : {};
        const list = Array.isArray(payload.data) ? payload.data : [];
        const mapped = list.map((item, index) => ({
          id: item.id || `${nextPage}-${index}`,
          title: item.vod_name || item.vod_name_zh_cn || item.vod_name_yn_yu || `Video ${index + 1}`,
          meta: item.vod_name_yn_yu || item.vod_name || "",
          poster: resolveAssetUrl(item.vod_pic),
          url: resolveAssetUrl(item.vod_play_url),
          raw: item,
        }));
        this.total = Number(payload.count || mapped.length || 0);
        this.videoItems = reset ? mapped : this.videoItems.concat(mapped);
        this.page = nextPage + 1;
        this.$nextTick(() => {
          this.refreshViewportHeight();
          this.syncCurrentVideo();
        });
      } catch (error) {
        if (reset) {
          this.videoItems = [];
        }
      } finally {
        this.loading = false;
      }
    },
    refreshViewportHeight() {
      const feed = this.$refs.feed;
      if (!feed) {
        return;
      }
      const height = window.innerHeight || document.documentElement.clientHeight || 0;
      feed.style.setProperty("--short-vh", `${height}px`);
    },
    getVideoEl(index) {
      const ref = this.$refs[`video-${index}`];
      return Array.isArray(ref) ? ref[0] : ref;
    },
    pauseAll() {
      this.videoItems.forEach((item, index) => {
        const el = this.getVideoEl(index);
        if (el) {
          el.pause();
          this.$set(this.playingMap, index, false);
        }
      });
    },
    syncCurrentVideo() {
      this.pauseAll();
      const current = this.getVideoEl(this.currentIndex);
      if (!current || !current.src) {
        return;
      }
      current.muted = this.isMuted;
      const playPromise = current.play();
      if (playPromise && playPromise.catch) {
        playPromise.catch(() => {
          this.$set(this.playingMap, this.currentIndex, false);
        });
      }
      this.$set(this.playingMap, this.currentIndex, true);
    },
    toggleVideo(index) {
      const current = this.getVideoEl(index);
      if (!current) {
        return;
      }
      this.currentIndex = index;
      if (current.paused) {
        current.muted = this.isMuted;
        const playPromise = current.play();
        if (playPromise && playPromise.catch) {
          playPromise.catch(() => {});
        }
        this.$set(this.playingMap, index, true);
        return;
      }
      current.pause();
      this.$set(this.playingMap, index, false);
    },
    toggleMute() {
      this.isMuted = !this.isMuted;
      this.videoItems.forEach((item, index) => {
        const el = this.getVideoEl(index);
        if (el) {
          el.muted = this.isMuted;
        }
      });
    },
    handleFeedScroll(event) {
      const el = event.target;
      const height = el.clientHeight || 1;
      const nextIndex = Math.round(el.scrollTop / height);
      if (nextIndex !== this.currentIndex && nextIndex >= 0 && nextIndex < this.videoItems.length) {
        this.currentIndex = nextIndex;
        this.syncCurrentVideo();
      }
      if (!this.loading && this.videoItems.length < this.total && el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
        this.fetchVideos(false);
      }
    },
    scrollNext(index) {
      const feed = this.$refs.feed;
      if (!feed) {
        return;
      }
      const height = feed.clientHeight || 0;
      feed.scrollTo({
        top: (index + 1) * height,
        behavior: "smooth",
      });
    },
    openAgent(link) {
      openKefuContact(this, { url: link });
    },
  },
};
</script>

<style lang="less" scoped>
.short-page {
  --short-tabbar-offset: calc(4.875rem + env(safe-area-inset-bottom));
  min-height: 100vh;
  min-height: 100dvh;
  background: #05030a;
  color: #fff;
}

.short-shell {
  width: 100%;
}

.short-feed {
  height: calc(100vh - var(--short-tabbar-offset));
  height: calc(100dvh - var(--short-tabbar-offset));
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  scroll-behavior: smooth;
  -ms-overflow-style: none;
  scrollbar-width: none;
  padding-bottom: var(--short-tabbar-offset);
}

.short-feed::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.short-slide,
.short-empty {
  position: relative;
  width: 100%;
  min-height: calc(var(--short-vh, 100vh) - var(--short-tabbar-offset));
  scroll-snap-align: start;
}

.short-media {
  position: relative;
  width: 100%;
  min-height: calc(var(--short-vh, 100vh) - var(--short-tabbar-offset));
  overflow: hidden;
  background: #05030a;
}

.short-video,
.short-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.short-video {
  object-fit: cover;
  background: #05030a;
}

.short-overlay {
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.04) 22%, rgba(0, 0, 0, 0.2) 60%, rgba(0, 0, 0, 0.84) 100%);
}

.short-topbar,
.short-bottom,
.short-hint,
.next-tip,
.pause-mask {
  position: absolute;
  z-index: 2;
}

.short-topbar {
  top: max(0.8rem, env(safe-area-inset-top));
  left: 0;
  right: 0;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 0.75rem;
  padding: 0 0.75rem;
}

.brand {
  justify-self: start;
  border: 0;
  padding: 0;
  background: transparent;
  color: #fff;
  font-size: 1.08rem;
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.04em;
}

.brand em {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.45rem;
  margin-left: 0.12rem;
  border-radius: 0.42rem;
  background: linear-gradient(180deg, #ff5c9b, #ff3f78);
  color: #180612;
  font-style: normal;
}

.short-topbar b {
  justify-self: center;
  font-size: 1.1rem;
  font-weight: 900;
}

.mute-btn {
  justify-self: end;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  background: rgba(20, 14, 29, 0.64);
  color: #fff;
}

.mute-btn svg {
  width: 1.1rem;
  height: 1.1rem;
}

.short-bottom {
  left: 0.75rem;
  right: 0.75rem;
  bottom: 0.52rem;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.8rem;
}

.short-copy {
  min-width: 0;
  flex: 1;
}

.short-author {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.45rem;
}

.short-author span {
  font-size: 1rem;
  font-weight: 900;
}

.short-author i {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.95rem;
  height: 1.2rem;
  padding: 0 0.48rem;
  border-radius: 999px;
  background: rgba(33, 231, 149, 0.24);
  color: #9cf0c4;
  font-size: 0.67rem;
  font-style: normal;
  font-weight: 800;
}

.short-copy h2 {
  margin: 0;
  font-size: 1.08rem;
  font-weight: 900;
}

.short-copy p {
  margin: 0.35rem 0 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.8rem;
  line-height: 1.5;
}

.short-meta {
  display: inline-flex;
  align-items: center;
  gap: 0.22rem;
}

.short-meta svg {
  width: 0.8rem;
  height: 0.8rem;
  flex: 0 0 auto;
  color: #ff8d55;
}

.contact-btn {
  min-width: 3.6rem;
  height: 3.6rem;
  border: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff6da3, #ff417f);
  box-shadow: 0 0.95rem 1.8rem rgba(255, 71, 128, 0.28);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 900;
}

.short-hint {
  left: 0.75rem;
  bottom: calc(4.9rem + 0.65rem + env(safe-area-inset-bottom));
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.hint-avatar {
  font-size: 1.15rem;
}

.hint-copy b {
  display: block;
  font-size: 0.98rem;
  font-weight: 900;
}

.hint-copy p {
  margin: 0.12rem 0 0;
  color: rgba(255, 160, 160, 0.95);
  font-size: 0.72rem;
  font-weight: 700;
}

.next-tip {
  left: 50%;
  bottom: 1.3rem;
  transform: translateX(-50%);
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.72);
  font-size: 1rem;
  font-weight: 900;
}

.pause-mask {
  inset: 0;
  margin: auto;
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
  background: rgba(10, 7, 17, 0.48);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.pause-mask svg {
  width: 1.4rem;
  height: 1.4rem;
}

.short-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  color: rgba(255, 255, 255, 0.82);
}

.short-empty b {
  font-size: 1.15rem;
}

.service-popup {
  width: min(21.5rem, calc(100vw - 2rem));
  border-radius: 1.55rem;
  background: linear-gradient(180deg, rgba(38, 18, 47, 0.98), rgba(22, 12, 32, 0.98));
}

.service-card {
  position: relative;
  padding: 1.2rem 1rem 1rem;
}

.service-close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 2rem;
  height: 2rem;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.72);
  font-size: 1.2rem;
}

.service-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.7rem;
  height: 3.7rem;
  margin: 0 auto 0.9rem;
  border-radius: 1rem;
  background: linear-gradient(135deg, #37c0ff, #3490ff);
}

.service-icon svg,
.service-item-icon svg {
  width: 1.7rem;
  height: 1.7rem;
}

.service-card h3 {
  margin: 0;
  text-align: center;
  font-size: 1.3rem;
  font-weight: 900;
}

.service-sub {
  margin: 0.7rem auto 0;
  max-width: 17.5rem;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.8rem;
  line-height: 1.55;
  text-align: center;
}

.service-item {
  display: flex;
  align-items: center;
  gap: 0.72rem;
  width: 100%;
  margin-top: 0.72rem;
  padding: 0.85rem 0.9rem;
  border: 1px solid rgba(255, 93, 149, 0.35);
  border-radius: 1rem;
  background: rgba(28, 18, 36, 0.92);
  color: #fff;
  text-align: left;
}

.service-item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.7rem;
  height: 2.7rem;
  border-radius: 0.85rem;
  background: linear-gradient(135deg, #37c0ff, #3490ff);
  color: #fff;
  flex-shrink: 0;
}

.service-item-copy {
  min-width: 0;
  flex: 1;
}

.service-item-copy b {
  display: block;
  font-size: 0.95rem;
  font-weight: 800;
}

.service-item-copy small {
  display: block;
  margin-top: 0.18rem;
  color: #74ffc4;
  font-size: 0.7rem;
}

.service-item em {
  color: #ff77aa;
  font-size: 0.76rem;
  font-style: normal;
  font-weight: 800;
}

.service-arrow {
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
}

.later-btn {
  width: 100%;
  min-height: 2.85rem;
  margin-top: 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.9rem;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  font-weight: 800;
}

button {
  cursor: pointer;
}

@media (min-width: 48rem) {
  .short-shell {
    width: 25rem;
    margin: 0 auto;
  }
}

</style>
